import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  Button,
  Space,
  Typography,
  message,
  Avatar,
  Tag,
  Progress,
  Modal,
} from 'antd';
import {
  UploadOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { api, uploadToS3, getS3Url } from '../../services/api';
import AppLayout from '../../components/Layout/AppLayout';
import CreatorModal from '../../components/Modals/CreatorModal';
import UserPersonaModal from '../../components/Modals/UserPersonaModal';
import { getUserId } from '../../utils/auth';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const TemplateFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [creators, setCreators] = useState([]);
  const [userPersonas, setUserPersonas] = useState([]);
  const [mimsTypes, setMimsTypes] = useState([]);
  const [userId] = useState(getUserId());

  // File upload states
  const [coverPicFileList, setCoverPicFileList] = useState([]);
  const [previewPicFileList, setPreviewPicFileList] = useState([]);
  const [templateFileList, setTemplateFileList] = useState([]);
  const [s3Keys, setS3Keys] = useState({
    coverPicS3key: '',
    previewPicS3key: '',
    templateFileS3key: '',
  });
  const [uploadStatus, setUploadStatus] = useState({
    coverPic: { uploading: false, error: null },
    previewPic: { uploading: false, error: null },
    templateFile: { uploading: false, error: null },
  });

  // Explicit state for selects
  const [creatorIdValue, setCreatorIdValue] = useState(null);
  const [userPersonasValue, setUserPersonasValue] = useState([]);

  // Submit enable/disable
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  // Modal states
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [showUserPersonaModal, setShowUserPersonaModal] = useState(false);

  // ---------- Helpers ----------

  const isFormComplete = (values, s3KeysState, uploadStatusState) => {
    console.log('===== FORM VALIDATION CHECK =====');

    console.log('Form Values:', values);
    console.log('CreatorId state:', creatorIdValue);
    console.log('UserPersonas state:', userPersonasValue);
    console.log('S3 Keys:', s3KeysState);
    console.log('Upload Status:', uploadStatusState);

    const requiredChecks = {
      title: !!values.title,
      price: values.price !== undefined && values.price !== null,
      format: !!values.format,
      templateFileUploaded: !!s3KeysState.templateFileS3key,
      noUploadInProgress:
        !uploadStatusState.coverPic.uploading &&
        !uploadStatusState.previewPic.uploading &&
        !uploadStatusState.templateFile.uploading,
    };

    console.log('Field-by-field status:', requiredChecks);

    Object.entries(requiredChecks).forEach(([key, passed]) => {
      if (!passed) console.warn(`❌ Missing or invalid: ${key}`);
    });

    const isValid = Object.values(requiredChecks).every(Boolean);
    console.log('FINAL VALID STATUS →', isValid ? '✅ VALID' : '❌ INVALID');
    console.log('=================================');

    return isValid;
  };

  // ---------- Fetch data ----------

  const fetchCreators = useCallback(async () => {
    try {
      const response = await api.getCreators();
      if (response.data.success) {
        setCreators(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
    }
  }, []);

  const fetchUserPersonas = useCallback(async () => {
    try {
      const response = await api.getUserPersonas();
      if (response.data.success) {
        setUserPersonas(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching user personas:', error);
    }
  }, []);

  const fetchMimsTypes = useCallback(async () => {
    try {
      const response = await api.getMimsTypes();
      if (response.data.success) {
        setMimsTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching MIMS types:', error);
    }
  }, []);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.getTemplate(id);
      if (response.data.success) {
        const template = response.data.data;
        const initialUserPersonaIds =
          template.userPersonas?.map((up) => up.id) || [];

        form.setFieldsValue({
          title: template.title,
          description: template.description,
          templateNotes: template.templateNotes,
          price: template.price,
          format: template.format || template.tags, // Support both old and new field names
          published: template.published,
          featured: template.featured,
          creatorId: template.creatorId,
          userPersonas: initialUserPersonaIds,
        });

        setCreatorIdValue(template.creatorId || null);
        setUserPersonasValue(initialUserPersonaIds);

        setS3Keys({
          coverPicS3key: template.coverPicS3key || '',
          previewPicS3key: template.previewPicS3key || '',
          templateFileS3key: template.templateFileS3key || '',
        });

        // Set file lists with existing images for preview (for edit mode)
        if (template.coverPicS3key) {
          const coverPicUrl = getS3Url(template.coverPicS3key);
          setCoverPicFileList([
            {
              uid: '-1',
              name: 'cover-pic.jpg',
              status: 'done',
              url: coverPicUrl,
              thumbUrl: coverPicUrl,
            },
          ]);
        }
        if (template.previewPicS3key) {
          const previewPicUrl = getS3Url(template.previewPicS3key);
          setPreviewPicFileList([
            {
              uid: '-2',
              name: 'preview-pic.jpg',
              status: 'done',
              url: previewPicUrl,
              thumbUrl: previewPicUrl,
            },
          ]);
        }
        if (template.templateFileS3key) {
          const templateFileUrl = getS3Url(template.templateFileS3key);
          setTemplateFileList([
            {
              uid: '-3',
              name:
                template.templateFileS3key.split('/').pop() ||
                'template-file',
              status: 'done',
              url: templateFileUrl,
            },
          ]);
        }
      }
    } catch (error) {
      message.error('Failed to load template');
      navigate('/roleplays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreators();
    fetchUserPersonas();
    fetchMimsTypes();

    if (isEditMode) {
      fetchTemplate();
    }
  }, [isEditMode, id, fetchCreators, fetchUserPersonas, fetchMimsTypes]);

  // ---------- MIMS label formatter ----------

  const formatMimsTypeLabel = (type) => {
    if (typeof type === 'string') {
      return type
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    }
    if (type && typeof type === 'object' && type.label) {
      return type.label;
    }
    if (type && typeof type === 'object' && type.value) {
      return type.value
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    }
    if (type === null || type === undefined) {
      return '';
    }
    return String(type);
  };

  // ---------- Upload handlers ----------

  const handleFileUpload = async (file, fileType) => {
    const setFileListState = {
      coverPic: setCoverPicFileList,
      previewPic: setPreviewPicFileList,
      templateFile: setTemplateFileList,
    };

    let previewUrl = null;
    if (file.type.startsWith('image/')) {
      previewUrl = URL.createObjectURL(file);
    }

    const newFile = {
      uid: file.uid || Date.now(),
      name: file.name,
      status: 'uploading',
      originFileObj: file,
      url: previewUrl,
      thumbUrl: previewUrl,
      percent: 0,
    };
    setFileListState[fileType]([newFile]);

    setUploadStatus((prev) => ({
      ...prev,
      [fileType]: { uploading: true, error: null },
    }));

    try {
      // Map fileType to API fileType parameter
      const apiFileType = {
        templateFile: 'templatefile',
        coverPic: 'coverpic',
        previewPic: 'previewpic',
      }[fileType];

      const uploadResult = await uploadToS3(file, apiFileType);
      const s3Key = uploadResult?.s3Key || (typeof uploadResult === 'string' ? uploadResult : null);
      const publicUrl = uploadResult?.publicUrl || null;
      const s3KeyName = `${fileType}S3key`;

      const displayUrl = publicUrl || getS3Url(s3Key);
      const isValidUrl =
        displayUrl &&
        (displayUrl.startsWith('https://') || displayUrl.startsWith('https://'));

      if (previewUrl && isValidUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setFileListState[fileType]([
        {
          ...newFile,
          status: 'done',
          url: isValidUrl ? displayUrl : previewUrl,
          thumbUrl: isValidUrl ? displayUrl : previewUrl,
          percent: 100,
        },
      ]);

      setS3Keys((prev) => ({
        ...prev,
        [s3KeyName]: s3Key,
      }));

      setUploadStatus((prev) => ({
        ...prev,
        [fileType]: { uploading: false, error: null },
      }));
      message.success(`${fileType} uploaded successfully!`);
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setFileListState[fileType]([]);
      setUploadStatus((prev) => ({
        ...prev,
        [fileType]: { uploading: false, error: error.message },
      }));
      message.error(`Failed to upload ${fileType}: ${error.message}`);
    }
  };

  const handleFileChange = (fileType) => (info) => {
    if (info.file.status === 'removed') {
      const setFileListState = {
        coverPic: setCoverPicFileList,
        previewPic: setPreviewPicFileList,
        templateFile: setTemplateFileList,
      };

      const currentFile = info.file;
      if (currentFile.url && currentFile.url.startsWith('blob:')) {
        URL.revokeObjectURL(currentFile.url);
      }
      if (currentFile.thumbUrl && currentFile.thumbUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentFile.thumbUrl);
      }

      setFileListState[fileType]([]);
      const s3KeyName = `${fileType}S3key`;
      setS3Keys((prev) => ({ ...prev, [s3KeyName]: '' }));
      setUploadStatus((prev) => ({
        ...prev,
        [fileType]: { uploading: false, error: null },
      }));
      return;
    }

    const setFileListState = {
      coverPic: setCoverPicFileList,
      previewPic: setPreviewPicFileList,
      templateFile: setTemplateFileList,
    };

    if (info.fileList && info.fileList.length > 0) {
      setFileListState[fileType](info.fileList);
    }
  };

  const beforeUpload = (file, fileType) => {
    if (fileType !== 'templateFile') {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return Upload.LIST_IGNORE;
      }
    }
    return false;
  };

  // ---------- Submit ----------

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const isUploading =
        uploadStatus.coverPic.uploading ||
        uploadStatus.previewPic.uploading ||
        uploadStatus.templateFile.uploading;

      if (isUploading) {
        message.warning('Please wait for file uploads to complete');
        setLoading(false);
        return;
      }

      // Validate required template file S3 key
      if (!s3Keys.templateFileS3key) {
        message.error('Please upload a template file');
        setLoading(false);
        return;
      }

      console.log('Submit values:', values);
      console.log('S3 keys:', s3Keys);
      console.log('creatorIdValue:', creatorIdValue);
      console.log('userPersonasValue:', userPersonasValue);

      // Create JSON payload with S3 keys
      const payload = {
        title: values.title,
        description: values.description || null,
        templateNotes: values.templateNotes || null,
        price: values.price !== null && values.price !== undefined ? parseFloat(values.price) : null,
        format: values.format,
        published: values.published || false,
        featured: values.featured || false,
        creatorId: creatorIdValue ?? null,
        templateFileS3key: s3Keys.templateFileS3key || null,
        coverPicS3key: s3Keys.coverPicS3key || null,
        previewPicS3key: s3Keys.previewPicS3key || null,
        userPersonaIds: (userPersonasValue || []).map((id) => Number(id)),
      };

      console.log('Final payload:', payload);

      let response;
      if (isEditMode) {
        response = await api.updateTemplate(id, payload);
      } else {
        response = await api.createTemplate(payload);
      }

      if (response.data.success) {
        message.success(
          `Template ${isEditMode ? 'updated' : 'created'} successfully!`
        );
        navigate('/roleplays');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error(
        error.response?.data?.message || 'Failed to save template'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCreator = creatorIdValue
    ? creators.find((c) => c.id === Number(creatorIdValue))
    : null;

  const renderUploadStatus = (fileType) => {
    const status = uploadStatus[fileType];
    if (status.uploading) {
      return (
        <div style={{ marginTop: '8px' }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Progress percent={100} status="active" showInfo={false} />
            <Space>
              <LoadingOutlined />
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                Uploading to S3... Please wait for 200 response
              </span>
            </Space>
            <span style={{ fontSize: '12px', color: '#999' }}>
              This may take a few moments depending on file size
            </span>
          </Space>
        </div>
      );
    }
    if (status.error) {
      return (
        <div style={{ marginTop: '8px' }}>
          <Tag color="error">Upload Failed: {status.error}</Tag>
        </div>
      );
    }
    if (s3Keys[`${fileType}S3key`]) {
      return (
        <div style={{ marginTop: '8px' }}>
          <Tag icon={<CheckCircleOutlined />} color="success">
            ✓ Successfully uploaded (200 OK)
          </Tag>
        </div>
      );
    }
    return null;
  };

  // Recalculate submit enabled when form values / s3Keys / select state change   
  useEffect(() => {
    const values = form.getFieldsValue();
    setIsSubmitDisabled(!isFormComplete(values, s3Keys, uploadStatus));
  }, [s3Keys, uploadStatus, creatorIdValue, userPersonasValue, form]);

  // ---------- Render ----------

  return (
    <AppLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Title level={2} style={{ margin: 0 }}>
                {isEditMode ? 'Edit Template' : 'Create Template'}
              </Title>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/roleplays')}
              >
                Back
              </Button>
            </div>

            {userId && <Tag color="blue">User ID: {userId}</Tag>}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              onValuesChange={(_, allValues) => {
                setIsSubmitDisabled(
                  !isFormComplete(allValues, s3Keys, uploadStatus)
                );
              }}
            >
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter title' }]}
              >
                <Input placeholder="Enter template title" size="large" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <TextArea rows={4} placeholder="Enter template description" />
              </Form.Item>

              <Form.Item
                name="templateNotes"
                label="Template Notes"
                rules={[{ required: true, message: 'Please enter template notes' }]}
              >
                <TextArea placeholder="Additional notes/instructions about the template" />
              </Form.Item>

              <Form.Item name="price" label="Price">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter price"
                  min={0}
                  step={0.01}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="format"
                label="Format"
                rules={[{ required: true, message: 'Please select a format' }]}
              >
                <Select placeholder="Select Format" size="large" allowClear>
                  {mimsTypes.map((type) => {
                    const value =
                      typeof type === 'string' ? type : type?.value || type;
                    const label = formatMimsTypeLabel(type);
                    return (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              {/* Creator */}
              <Form.Item label="Creator">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    name="creatorId"
                    noStyle
                    rules={[
                      { required: true, message: 'Please select a creator' },
                    ]}
                  >
                    <Select
                      placeholder="Select a creator"
                      size="large"
                      style={{ flex: 1 }}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      value={creatorIdValue}
                      onChange={(val) => {
                        setCreatorIdValue(val || null);
                        form.setFieldsValue({ creatorId: val || null });
                      }}
                      filterOption={(input, option) =>
                        (option?.children ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {creators.map((creator) => (
                        <Option key={creator.id} value={creator.id}>
                          {creator.creatorName}
                          {creator.creatorJobTitle
                            ? ` - ${creator.creatorJobTitle}`
                            : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setShowCreatorModal(true)}
                  >
                    + Add
                  </Button>
                </Space.Compact>

                {selectedCreator && selectedCreator.creatorPicS3key && (
                  <div style={{ marginTop: '12px' }}>
                    <Space>
                      <Avatar
                        src={selectedCreator.creatorPicS3key}
                        size={48}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          {selectedCreator.creatorName}
                        </div>
                        {selectedCreator.creatorJobTitle && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {selectedCreator.creatorJobTitle}
                          </div>
                        )}
                      </div>
                    </Space>
                  </div>
                )}
              </Form.Item>

              {/* User Personas */}
              <Form.Item label="User Personas">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    name="userPersonas"
                    noStyle
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select user personas"
                      size="large"
                      style={{ flex: 1 }}
                      allowClear
                      showSearch
                      value={userPersonasValue}
                      onChange={(vals) => {
                        setUserPersonasValue(vals || []);
                        form.setFieldsValue({ userPersonas: vals || [] });
                      }}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                    >
                      {userPersonas.map((persona) => (
                        <Option key={persona.id} value={persona.id}>
                          {persona.userPersona}
                          {persona.description
                            ? ` - ${persona.description}`
                            : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setShowUserPersonaModal(true)}
                  >
                    + Add
                  </Button>
                </Space.Compact>
              </Form.Item>

              <Form.Item label="Cover Picture">
                <div>
                  <Upload
                    fileList={coverPicFileList}
                    onChange={handleFileChange('coverPic')}
                    beforeUpload={(file) => {
                      const result = beforeUpload(file, 'coverPic');
                      if (result === false) {
                        handleFileUpload(file, 'coverPic');
                      }
                      return false; // Prevent auto upload
                    }}
                    maxCount={1}
                    accept="image/*"
                    listType="picture-card"
                    onPreview={(file) => {
                      const imageUrl = file.url || file.thumbUrl;
                      if (imageUrl) {
                        Modal.info({
                          title: null,
                          width: '90%',
                          style: { top: 20, paddingBottom: 0 },
                          content: (
                            <div style={{ textAlign: 'center' }}>
                              <img
                                src={imageUrl}
                                alt={file.name}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '80vh',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
                                  message.error('Failed to load image preview');
                                }}
                              />
                            </div>
                          ),
                          okText: 'Close',
                          maskClosable: true,
                        });
                      } else {
                        message.warning('Image URL not available');
                      }
                    }}
                    showUploadList={{
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                      showDownloadIcon: false,
                    }}
                  >
                    {coverPicFileList.length < 1 && (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                  {renderUploadStatus('coverPic')}
                </div>
              </Form.Item>

              <Form.Item label="Preview Picture">
                <div>
                  <Upload
                    fileList={previewPicFileList}
                    onChange={handleFileChange('previewPic')}
                    beforeUpload={(file) => {
                      const result = beforeUpload(file, 'previewPic');
                      if (result === false) {
                        handleFileUpload(file, 'previewPic');
                      }
                      return false; // Prevent auto upload
                    }}
                    maxCount={1}
                    accept="image/*"
                    listType="picture-card"
                    onPreview={(file) => {
                      const imageUrl = file.url || file.thumbUrl;
                      if (imageUrl) {
                        Modal.info({
                          title: null,
                          width: '90%',
                          style: { top: 20, paddingBottom: 0 },
                          content: (
                            <div style={{ textAlign: 'center' }}>
                              <img
                                src={imageUrl}
                                alt={file.name}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '80vh',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
                                  message.error('Failed to load image preview');
                                }}
                              />
                            </div>
                          ),
                          okText: 'Close',
                          maskClosable: true,
                        });
                      } else {
                        message.warning('Image URL not available');
                      }
                    }}
                    showUploadList={{
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                      showDownloadIcon: false,
                    }}
                  >
                    {previewPicFileList.length < 1 && (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                  {renderUploadStatus('previewPic')}
                </div>
              </Form.Item>

              <Form.Item label="Template File">
                <div>
                  <Upload
                    fileList={templateFileList}
                    onChange={handleFileChange('templateFile')}
                    beforeUpload={(file) => {
                      const result = beforeUpload(file, 'templateFile');
                      if (result === false) {
                        handleFileUpload(file, 'templateFile');
                      }
                      return false; // Prevent auto upload
                    }}
                    maxCount={1}
                    showUploadList={{
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                      showDownloadIcon: false,
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      size="large"
                    >
                      Upload Template File
                    </Button>
                  </Upload>
                  {renderUploadStatus('templateFile')}
                </div>
              </Form.Item>

              <Form.Item name="published" valuePropName="checked">
                <Switch checkedChildren="Published" unCheckedChildren="Draft" />
              </Form.Item>

              <Form.Item name="featured" valuePropName="checked">
                <Switch
                  checkedChildren="Featured"
                  unCheckedChildren="Not Featured"
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={<SaveOutlined />}
                    loading={loading}
                    disabled={isSubmitDisabled}
                  >
                    {isEditMode ? 'Update Template' : 'Create Template'}
                  </Button>
                  <Button size="large" onClick={() => navigate('/roleplays')}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      </div>

      <CreatorModal
        visible={showCreatorModal}
        onCancel={() => setShowCreatorModal(false)}
        onSuccess={fetchCreators}
      />

      <UserPersonaModal
        visible={showUserPersonaModal}
        onCancel={() => setShowUserPersonaModal(false)}
        onSuccess={fetchUserPersonas}
      />
    </AppLayout>
  );
};

export default TemplateFormPage;
